import { motion } from 'framer-motion'
import { Phone, Mail, Clock, MapPin, ExternalLink } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { pageUrl } from '../seo'

const fadeUp = {
    hidden: { opacity: 0, y: 30 },
    visible: { opacity: 1, y: 0 }
}

export default function ApoioAPAV() {
    const { t } = useTranslation()

    return (
        <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.6 }}
            className="min-h-screen bg-white"
        >
            {/* SEO por rota — no React 19 estas tags são içadas para o <head> */}
            <title>{t('apoio.metaTitle')}</title>
            <meta name="description" content={t('apoio.metaDescription')} />
            <link rel="canonical" href={pageUrl('apoio-apav')} />
            {/* Hero Section */}
            <section
                className="relative overflow-hidden py-16 lg:py-20 bg-cover bg-center"
                style={{ backgroundImage: "url('/assets/images/hero-bg.jpg')" }}
            >
                <div className="absolute inset-0 bg-black/55" />
                <div className="container-custom relative z-10">
                    <motion.div
                        initial={{ opacity: 0, y: 40 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.7, delay: 0.1 }}
                    >
                        <h1 className="text-4xl lg:text-6xl font-bold text-white mb-6 leading-tight">
                            {t('apoio.title')}
                        </h1>
                        <p className="text-lg text-slate-300 leading-relaxed max-w-3xl mb-8">
                            {t('apoio.lead1')}
                        </p>
                        <p className="text-lg text-slate-300 leading-relaxed max-w-3xl mb-8">
                            {t('apoio.lead2')}
                        </p>
                        <a
                            href="https://apav.pt/sobre-a-apav/contactos/"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-2 px-6 py-3 rounded-xl bg-white text-brand-700 font-bold hover:bg-brand-50 transition-colors shadow-lg"
                        >
                            {t('apoio.findService')}
                            <ExternalLink size={16} aria-hidden="true" />
                        </a>
                    </motion.div>
                </div>
            </section>

            {/* APAV SAFE Section */}
            <section className="section-padding bg-slate-50">
                <div className="container-custom">
                    <motion.div
                        variants={fadeUp}
                        initial="hidden"
                        whileInView="visible"
                        viewport={{ once: true }}
                        transition={{ duration: 0.6 }}
                        className="grid md:grid-cols-2 gap-12 items-start"
                    >
                        <div>
                            <h2 className="text-2xl lg:text-3xl font-bold text-slate-900 mb-6" translate="no">
                                {t('apoio.safe.title')}
                            </h2>
                            <div className="space-y-4 text-slate-700 leading-relaxed">
                                <p>{t('apoio.safe.p1')}</p>
                                <p>{t('apoio.safe.p2')}</p>
                            </div>
                        </div>

                        <div className="flex flex-col gap-6">
                            <img
                                src="/assets/images/logo-apavsafe.png"
                                alt={t('apoio.safe.logoAlt')}
                                className="w-full max-w-sm"
                            />
                            {/* Morada, horário, telefone e email ficam no código: não se
                                traduzem, e a MT já partiu contactos noutros sites. */}
                            <div className="bg-white rounded-2xl p-8 shadow-md border border-slate-100">
                                <div className="space-y-5">
                                    <div className="flex items-start gap-3">
                                        <div className="w-9 h-9 bg-brand-50 rounded-xl flex items-center justify-center shrink-0 border border-brand-100">
                                            <MapPin size={17} className="text-brand-600" aria-hidden="true" />
                                        </div>
                                        <div className="text-slate-700 text-base leading-relaxed" translate="no">
                                            <span className="sr-only">{t('apoio.safe.addressAria')}: </span>
                                            <p>Rua José Estêvão, 135 A</p>
                                            <p>1150-201 LISBOA</p>
                                        </div>
                                    </div>
                                    <div className="flex items-start gap-3">
                                        <div className="w-9 h-9 bg-brand-50 rounded-xl flex items-center justify-center shrink-0 border border-brand-100">
                                            <Clock size={17} className="text-brand-600" aria-hidden="true" />
                                        </div>
                                        <div className="text-slate-700">
                                            <span className="sr-only">{t('apoio.safe.hoursAria')}: </span>
                                            <p>{t('apoio.safe.weekdays')}</p>
                                            <p dir="ltr">10H00-13H00</p>
                                            <p dir="ltr">14H00-17H30</p>
                                        </div>
                                    </div>
                                    <div className="flex items-center gap-3">
                                        <div className="w-9 h-9 bg-brand-50 rounded-xl flex items-center justify-center shrink-0 border border-brand-100">
                                            <Phone size={17} className="text-brand-600" aria-hidden="true" />
                                        </div>
                                        <a href="tel:213587914" dir="ltr" className="text-slate-900 font-bold hover:text-brand-600 transition-colors">
                                            <span className="sr-only">{t('apoio.safe.phoneAria')}: </span>
                                            21 358 79 14
                                        </a>
                                    </div>
                                    <div className="flex items-center gap-3">
                                        <div className="w-9 h-9 bg-brand-50 rounded-xl flex items-center justify-center shrink-0 border border-brand-100">
                                            <Mail size={17} className="text-brand-600" aria-hidden="true" />
                                        </div>
                                        <a href="mailto:safe@apav.pt" dir="ltr" className="text-brand-600 font-bold hover:underline">
                                            <span className="sr-only">{t('apoio.safe.emailAria')}: </span>
                                            safe@apav.pt
                                        </a>
                                    </div>
                                    <div className="pt-2">
                                        <a
                                            href="mailto:safe@apav.pt"
                                            className="w-full flex items-center justify-center gap-2 px-5 py-3 rounded-xl bg-brand-600 text-white font-bold hover:bg-brand-700 transition-colors shadow-md"
                                        >
                                            {t('apoio.safe.contact')}
                                        </a>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </motion.div>
                </div>
            </section>
        </motion.div>
    )
}
